import { z } from 'zod';
import type { LoyaltyPointEventTypeToken } from '@sleed/bizcommerce-api';

/**
 * Loyalty points-per-entity edit form. Mirrors `LoyaltyPointEventTypeToken` and the legacy admin
 * grid (Core/client/admin/src/pages/System/PointSettings/assignPoints.js): the event-type name (nm)
 * and the updated date (crt) are read-only; description (descr), points (pnt) and the active-from/to
 * dates are editable. The discriminator fields (e_tp / s_cd) and crt are carried through unchanged so
 * an update never wipes them. The legacy grid is edit-only — it exposes no add and its CustomStore
 * has no remove, so delete is not offered here.
 */
/** LoyaltyPointsEventCode.NewAppointment: the only event whose points may be negative (an appointment that costs points). */
export const NEW_APPOINTMENT_EVENT = 11;

/**
 * The server's rules (PointsPerEntityController.Update): whole points, 0 or more except a new appointment; a start
 * date (without one the event never applies); an end after the start.
 */
export const loyaltyEventTypeSchema = z
	.object({
		id: z.number().optional(),
		descr: z.string().trim().max(500, 'validation.tooLong').nullable(),
		pnt: z.number({ message: 'validation.required' }).int('validation.wholeNumber'),
		afrom: z.string({ message: 'validation.required' }).min(1, 'validation.required'),
		ato: z.string().nullable(),
		// Carried through unchanged — not surfaced as editable fields.
		e_tp: z.number().optional(),
		s_cd: z.string().optional(),
		nm: z.string().optional(),
		crt: z.string().optional()
	})
	.superRefine((form, ctx) => {
		if (form.pnt < 0 && form.e_tp !== NEW_APPOINTMENT_EVENT) ctx.addIssue({ code: 'custom', path: ['pnt'], message: 'validation.wholeNumber' });
		if (form.ato && form.afrom && new Date(form.ato) < new Date(form.afrom))
			ctx.addIssue({ code: 'custom', path: ['ato'], message: 'validation.endBeforeStart' });
	});

export type LoyaltyEventTypeFormValues = z.infer<typeof loyaltyEventTypeSchema>;

export function tokenToForm(token: LoyaltyPointEventTypeToken): LoyaltyEventTypeFormValues {
	return {
		id: token.id,
		descr: token.descr ?? null,
		pnt: token.pnt ?? 0,
		afrom: token.afrom ? String(token.afrom) : '',
		ato: token.ato ? String(token.ato) : null,
		e_tp: token.e_tp,
		s_cd: token.s_cd ?? undefined,
		nm: token.nm ?? undefined,
		crt: token.crt ? String(token.crt) : undefined
	};
}

export function formToToken(form: LoyaltyEventTypeFormValues): LoyaltyPointEventTypeToken {
	return {
		id: form.id,
		e_tp: form.e_tp,
		s_cd: form.s_cd,
		nm: form.nm,
		descr: form.descr?.trim() || null,
		pnt: form.pnt,
		afrom: form.afrom || null,
		ato: form.ato || null,
		crt: form.crt
	};
}
